import React, { useEffect, useMemo, useState } from 'react';
import { gameState } from '../game/state/GameState';
import { GAME_STATES, TILE_STATUS } from '../game/constants';
import { getModelById } from '../game/voxel/models';
import { publicShellPayload } from '../game/voxel/slicePipeline';
import { BOOTCAMP_INTRO_STEPS } from '../game/config/bootcampConfig';
import { claimTile } from '../game/actions/TileActions';
import TileNode from './TileNodeV2';
import HologramViewport from './HologramViewport';
import { IconTools, IconWand, IconNodes } from './AtelierIcons';

function sliceLabel(tile) {
    const axis = tile?.data?.axis;
    const index = tile?.data?.index;
    if (axis != null) return `${String(axis).toUpperCase()}=${index}`;
    return tile?.data?.clue || tile?.id || 'slice';
}

const BootcampVault = ({ onFeedback, onComplete }) => {
    const [tiles, setTiles] = useState([]);
    const [isComplete, setIsComplete] = useState(false);
    const [introIdx, setIntroIdx] = useState(0);
    const [introDone, setIntroDone] = useState(
        () => sessionStorage.getItem('vq_bootcamp_intro_done') === '1'
    );

    const heartShell = useMemo(() => {
        const model = getModelById('heart_8_tutorial');
        if (!model) return null;
        const payload = publicShellPayload(model, null);
        payload.voxels = (payload.voxels || []).map((v) => ({ ...v, revealed: true }));
        return { model, ...payload };
    }, []);

    useEffect(() => {
        const syncVault = () => {
            const gridArray = Array.from(gameState.vault.getTiles());
            setTiles(gridArray);
            if (gameState.getCurrentState() === GAME_STATES.BOOTCAMP_COMPLETE) {
                setIsComplete(true);
            }
        };

        syncVault();
        const interval = setInterval(syncVault, 500);
        return () => clearInterval(interval);
    }, []);

    // Block tile focus / other nav while the welcome cards are up.
    useEffect(() => {
        if (introDone) return undefined;
        gameState.clearSelection();
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [introDone]);

    const meId = gameState.currentUser?.id;
    const guidedTile = useMemo(
        () => tiles.find((t) => t.data?.tutorial?.guided) || null,
        [tiles]
    );
    const lessonDone = guidedTile?.status === TILE_STATUS.SOLVED;
    const heartTiles = useMemo(
        () => tiles.filter((t) => !t.data?.tutorial?.guided),
        [tiles]
    );
    const nextTargets = useMemo(() => {
        if (!lessonDone) {
            if (!guidedTile) return [];
            if (
                guidedTile.status === TILE_STATUS.OPEN ||
                (guidedTile.status === TILE_STATUS.CLAIMED && guidedTile.claimedBy === meId)
            ) {
                return [guidedTile];
            }
            return [];
        }
        return heartTiles.filter(
            (t) =>
                t.status === TILE_STATUS.OPEN ||
                (t.status === TILE_STATUS.CLAIMED && t.claimedBy === meId)
        );
    }, [lessonDone, guidedTile, heartTiles, meId]);

    const allowedIds = useMemo(
        () => new Set(nextTargets.map((t) => t.id)),
        [nextTargets]
    );
    const highlightIds = useMemo(
        () =>
            new Set(
                nextTargets
                    .filter((t) => t.status === TILE_STATUS.OPEN)
                    .map((t) => t.id)
            ),
        [nextTargets]
    );
    const targetLabels = nextTargets
        .filter((t) => t.status === TILE_STATUS.OPEN)
        .map(sliceLabel);

    const handleGraduate = (action = 'lobby') => {
        if (onComplete) onComplete(action);
    };

    const finishIntro = () => {
        sessionStorage.setItem('vq_bootcamp_intro_done', '1');
        setIntroDone(true);
    };

    const openPracticeTile = async (tile) => {
        if (!allowedIds.has(tile.id)) {
            onFeedback?.(
                targetLabels.length
                    ? `Tap a glowing slice (${targetLabels.join(' or ')})`
                    : 'That workbench is locked for now'
            );
            return;
        }
        if (tile.status === TILE_STATUS.CLAIMED && tile.claimedBy === meId) {
            gameState.selectTile(tile.id);
            return;
        }
        if (tile.status !== TILE_STATUS.OPEN) return;

        const res = await claimTile(tile.id);
        if (!res.success) {
            gameState.selectTile(tile.id);
            onFeedback?.(res.error || 'Could not open slice');
            return;
        }
        onFeedback?.(
            tile.data?.tutorial?.guided
                ? 'Free practice board opened'
                : `Opened ${sliceLabel(tile)} · restore this Heart plane`
        );
    };

    const startFirstLesson = async () => {
        finishIntro();
        const first =
            tiles.find((t) => t.status === 'OPEN' && t.data?.tutorial?.guided) ||
            tiles.find((t) => t.status === 'OPEN');
        if (!first) {
            onFeedback?.('No practice slice left — open a workbench below');
            return;
        }
        await openPracticeTile(first);
    };

    const solved = tiles.filter((t) => t.status === 'SOLVED').length;
    const pct = tiles.length ? Math.round((solved / tiles.length) * 100) : 0;
    const intro = BOOTCAMP_INTRO_STEPS[introIdx] || BOOTCAMP_INTRO_STEPS[0];
    const isLastIntro = introIdx >= BOOTCAMP_INTRO_STEPS.length - 1;

    if (tiles.length === 0) {
        return (
            <div className="atelier-bootcamp atelier-bootcamp--loading">
                Preparing the apprentice bench…
            </div>
        );
    }

    // Full-screen welcome gate — only Back / Next (or Start) are interactive.
    if (!introDone) {
        return (
            <div
                className="atelier-bootcamp-intro-gate"
                role="dialog"
                aria-modal="true"
                aria-label="Bootcamp welcome"
            >
                <div className="atelier-bootcamp-intro-card atelier-parchment">
                    <div className="atelier-parchment-row">
                        <span className="atelier-stamp">{intro.stamp}</span>
                        <span className="atelier-session">
                            {introIdx + 1}/{BOOTCAMP_INTRO_STEPS.length}
                        </span>
                    </div>
                    <h2 className="atelier-mystery-title">{intro.title}</h2>
                    <p className="atelier-bootcamp-copy">{intro.body}</p>
                    <div className="atelier-bootcamp-intro-actions">
                        {introIdx > 0 ? (
                            <button
                                type="button"
                                className="atelier-chip-btn"
                                onClick={() => setIntroIdx((i) => Math.max(0, i - 1))}
                            >
                                Back
                            </button>
                        ) : (
                            <span className="atelier-bootcamp-intro-spacer" aria-hidden />
                        )}
                        {!isLastIntro ? (
                            <button
                                type="button"
                                className="atelier-btn-primary"
                                onClick={() => setIntroIdx((i) => i + 1)}
                            >
                                Next
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="atelier-btn-primary"
                                onClick={startFirstLesson}
                            >
                                Start free 4×4 lesson
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const heartGuideCopy =
        targetLabels.length === 0
            ? 'Finish any claimed plane, then graduate when every practice slice is done.'
            : targetLabels.length === 1
              ? `Tap the glowing workbench ${targetLabels[0]} — only that plane is open right now.`
              : `Tap a glowing workbench — ${targetLabels.join(' or ')}. Only those planes are clickable.`;

    return (
        <div className={`atelier-bootcamp${lessonDone ? ' is-heart-phase' : ' is-lesson-phase'}`}>
            <header className="atelier-bootcamp-top">
                <div className="atelier-brand atelier-brand--compact">
                    <span className="atelier-brand-mark" aria-hidden>
                        <IconWand />
                    </span>
                    <div>
                        <div className="atelier-brand-name">Voxel Atelier</div>
                        <div className="atelier-brand-sub">Guided Bootcamp · free practice</div>
                    </div>
                </div>
                <span className="atelier-chip atelier-chip--soft">
                    {solved}/{tiles.length} slices
                </span>
            </header>

            <section className="atelier-parchment atelier-bootcamp-parchment" aria-label="Lesson">
                <div className="atelier-parchment-row">
                    <span className="atelier-stamp">
                        {lessonDone ? '[HEART PLANES]' : '[PRACTICE]'}
                    </span>
                    <span className="atelier-session">Bootcamp</span>
                </div>
                <h2 className="atelier-mystery-title">
                    {!lessonDone
                        ? 'Open the 4×4 practice board'
                        : 'Keep restoring the Heart'}
                </h2>
                <p className="atelier-bootcamp-copy">
                    {!lessonDone
                        ? 'Tap the glowing Practice 4×4 below. Glowing cells show exactly what to DRAW, then MARK.'
                        : heartGuideCopy}
                </p>
                {!lessonDone && guidedTile?.status === TILE_STATUS.OPEN && (
                    <button
                        type="button"
                        className="atelier-btn-primary"
                        style={{ marginTop: 10, width: '100%', justifyContent: 'center' }}
                        onClick={() => openPracticeTile(guidedTile)}
                    >
                        Start free 4×4 lesson
                    </button>
                )}
                {lessonDone && targetLabels.length > 0 && (
                    <button
                        type="button"
                        className="atelier-btn-primary"
                        style={{ marginTop: 10, width: '100%', justifyContent: 'center' }}
                        onClick={() => {
                            const next = nextTargets.find((t) => t.status === TILE_STATUS.OPEN);
                            if (next) openPracticeTile(next);
                        }}
                    >
                        Open {targetLabels[0]}
                    </button>
                )}
            </section>

            <div className="atelier-bootcamp-progress">
                <div className="atelier-progress-labels">
                    <span>{pct}% apprenticeship</span>
                    <span>
                        {solved} / {tiles.length} planes
                    </span>
                </div>
                <div className="atelier-progress-track">
                    <div className="atelier-progress-fill" style={{ width: `${pct}%` }} />
                </div>
            </div>

            <section className="atelier-bootcamp-benches" aria-label="Tutorial slices">
                <div className="atelier-wb-head">
                    <h3>Practice workbenches</h3>
                    <p className="atelier-bootcamp-bench-note">
                        {lessonDone
                            ? 'Tap a glowing plane only'
                            : 'Tap the glowing 4×4 only'}
                    </p>
                </div>
                <div className="vault-grid atelier-bootcamp-grid">
                    {tiles.map((tile) => {
                        const isTarget = highlightIds.has(tile.id);
                        const isAllowed = allowedIds.has(tile.id);
                        const isSolved = tile.status === TILE_STATUS.SOLVED;
                        return (
                            <div
                                key={tile.id}
                                className={`atelier-bootcamp-bench${
                                    isTarget ? ' is-target' : ''
                                }${!isAllowed && !isSolved ? ' is-locked' : ''}`}
                            >
                                <TileNode
                                    tile={tile}
                                    highlighted={isTarget}
                                    dimmed={!isAllowed && !isSolved}
                                    disabled={!isAllowed}
                                    onSelectSlice={openPracticeTile}
                                />
                                {tile.data?.tutorial?.title && (
                                    <div className="atelier-bootcamp-bench-label">
                                        {isTarget
                                            ? `Tap · ${sliceLabel(tile)}`
                                            : isSolved
                                              ? `Done · ${sliceLabel(tile)}`
                                              : `${tile.data.tutorial.size}×${tile.data.tutorial.size}${
                                                    tile.data.tutorial.guided ? ' · guided' : ''
                                                }`}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            {isComplete && (
                <div className="atelier-bootcamp-graduate" role="dialog" aria-label="Training complete">
                    <div className="atelier-bootcamp-graduate-card">
                        <div className="atelier-reveal-pill">● TRAINING COMPLETE</div>
                        <h1 className="atelier-reveal-title">It was a Heart</h1>
                        <p className="atelier-reveal-sub">You learned slices + nonograms</p>

                        {heartShell && (
                            <div className="atelier-bootcamp-master">
                                <HologramViewport
                                    size={heartShell.size}
                                    voxels={heartShell.voxels}
                                    palette={heartShell.palette}
                                    grayscale={false}
                                    colorBlend={1}
                                    autoRotate
                                    presentation="trophy"
                                    height={180}
                                />
                            </div>
                        )}

                        <p className="atelier-bootcamp-graduate-copy">
                            Training done. Start on the Workshop floor — comfortable 8×8 slices.
                            Unlock Atelier later for denser 16³ carves.
                        </p>
                        <div className="atelier-bootcamp-cta-stack">
                            <button
                                type="button"
                                className="atelier-btn-primary atelier-bootcamp-cta"
                                onClick={() => handleGraduate('public')}
                            >
                                <IconNodes className="atelier-btn-ico" />
                                Enter Workshop
                            </button>
                            <button
                                type="button"
                                className="atelier-btn-secondary atelier-bootcamp-cta"
                                onClick={() => handleGraduate('create')}
                            >
                                <IconTools className="atelier-btn-ico" />
                                Create a Syndicate
                            </button>
                            <button
                                type="button"
                                className="atelier-bootcamp-cta-link"
                                onClick={() => handleGraduate('join')}
                            >
                                I have an invite code
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BootcampVault;
